import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import addDays from 'date-fns/addDays';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { translateDateFormat } from '../../../../../../shared/LanguageService';
import { PHOTOGRAPHER_BOOKING_DATE_FORMAT } from "../../shared/bookingDateSelect/state/dateHelper";

export type SendBookingRequestModalLabels = {
    title: string;
    text: string;
    info: string;
    date: string;
    sendButton: string;
    cancelButton: string;
};

type SendBookingRequestModalProps = {
    labels: SendBookingRequestModalLabels;
    show: boolean;
    onClose: () => void;
    onSend: (preferredDate: Date | null, notes: string) => void;
};

export function SendBookingRequestModal({
    onSend,
    onClose,
    labels,
    show
}: SendBookingRequestModalProps) {
    let [selectedDate, setSelectedDate] = React.useState<Date | null>(null);

    let today = new Date(Date.now());

    let formRef = React.useRef<HTMLFormElement>(null);
    let otherInfoRef = React.useRef<HTMLInputElement>(null);

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (formRef.current) {
            let preferredDate = selectedDate;

            let formData = new FormData(formRef.current);
            let notes = formData.get('notes') as string;

            formRef.current.reset();
            setSelectedDate(null);

            onSend(preferredDate, notes);
        }
    }

    return (
        <Dialog onClose={onClose} open={show}>
            <form ref={formRef} onSubmit={handleSubmit}>
                <DialogTitle>
                    {labels.title}
                    <IconButton
                        aria-label="close-send-request-modal"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        sx={{
                            fontSize: '16px',
                            fontFamily: 'Gilroy'
                        }}
                    >
                        {labels.text}
                    </DialogContentText>
                    <MobileDatePicker
                        label={labels.date}
                        inputFormat={PHOTOGRAPHER_BOOKING_DATE_FORMAT}
                        minDate={addDays(today, 1)}
                        name="preferredDate"
                        disablePast={true}
                        value={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        sx={{
                            '.MuiPickersToolbar-penIconButton': {
                                display: 'none'
                            }
                        }}
                        renderInput={({ inputRef, inputProps }) => {
                            let formattedDate = selectedDate
                                ? translateDateFormat(
                                      selectedDate,
                                      PHOTOGRAPHER_BOOKING_DATE_FORMAT
                                  )
                                : labels.date;
                            return (
                                <Box
                                    ref={inputRef}
                                    role="button"
                                    {...inputProps}
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        padding: '20px 0'
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontFamily: 'Gilroy',
                                            fontSize: '16px',
                                            fontWeight: '700',
                                            color: 'text.secondary',
                                            textTransform: 'capitalize',
                                            paddingRight: '20px'
                                        }}
                                    >
                                        <time dateTime={formattedDate}>{formattedDate}</time>
                                    </Typography>
                                    <CalendarMonthIcon />
                                </Box>
                            );
                        }}
                    />
                    <TextField
                        name="notes"
                        inputRef={otherInfoRef}
                        label={labels.info}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    aria-label="delete-text"
                                    onClick={() => {
                                        if (otherInfoRef.current) {
                                            otherInfoRef.current.value = '';
                                        }
                                    }}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        padding: 0
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            )
                        }}
                        fullWidth
                        multiline
                        minRows={3}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} variant="text">
                        {labels.cancelButton}
                    </Button>
                    <Button type="submit" variant="contained">
                        {labels.sendButton}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
